import React, { useState, useEffect } from "react";
import { Table, Button, Badge, Card, Row, Col, Spinner, Form, Pagination } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";

const OTAdminManagement = () => {
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 2).toISOString().split('T')[0];
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
  };

  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    fromDate: getFirstDayOfMonth(),
    toDate: getLastDayOfMonth(),
    status: "",
    createdBy: ""
  });

  useEffect(() => {
    fetchUsers();
    fetchOTRequests();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.users);
      console.log("DEBUG - User API response:", res.data);
      setUsers(res.data || []);
    } catch (err) { console.error("Load users error:", err); }
  };

  const fetchOTRequests = async () => {
    setLoading(true);
    try {
      const url = API_ENDPOINTS.filterOtRequests(filters);
      const res = await axiosClient.get(url);
      setRequests(res.data || []);
    } catch (err) { console.error("Load error:", err); } 
    finally { setLoading(false); }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = requests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(requests.length / itemsPerPage) || 1;

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const renderStatus = (status) => {
    const statusMap = { 0: ["DRAFT", "secondary"], 1: ["PENDING", "warning"], 2: ["APPROVED", "success"], 3: ["REJECTED", "danger"] };
    const [label, bg] = statusMap[status] || ["UNKNOWN", "dark"];
    return <Badge bg={bg} className="rounded-pill px-3 py-1">{label}</Badge>;
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString('vi-VN', { hour12: false });

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <h3 className="fw-bold text-dark mb-4 text-uppercase">OT Admin Management</h3>

      <Card className="shadow-sm border-0 rounded-3 mb-4">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={3}><Form.Label className="small fw-bold text-muted">FROM DATE</Form.Label><Form.Control type="date" value={filters.fromDate} onChange={(e) => setFilters(p => ({...p, fromDate: e.target.value}))} size="sm" /></Col>
            <Col md={3}><Form.Label className="small fw-bold text-muted">TO DATE</Form.Label><Form.Control type="date" value={filters.toDate} onChange={(e) => setFilters(p => ({...p, toDate: e.target.value}))} size="sm" /></Col>
            <Col md={3}><Form.Label className="small fw-bold text-muted">STATUS</Form.Label>
              <Form.Select value={filters.status} onChange={(e) => setFilters(p => ({...p, status: e.target.value}))} size="sm">
                <option value="">All Statuses</option><option value="1">Pending</option><option value="2">Approved</option><option value="3">Rejected</option>
              </Form.Select>
            </Col>
            <Col md={3}>
                <Form.Label className="small fw-bold text-muted">CREATED BY</Form.Label>
                <Form.Select value={filters.createdBy} onChange={(e) => setFilters(p => ({...p, createdBy: e.target.value}))} size="sm">
                    <option value="">All Users</option>
                    {users.map(u => (
                        <option key={u.id} value={u.id}>
                            {u.fullName || u.employeeName || u.userName}
                        </option>
                    ))}
                </Form.Select>
            </Col>
            <Col md={12} className="mt-3">
                <Button variant="outline-secondary" size="sm" onClick={() => setFilters({fromDate: getFirstDayOfMonth(), toDate: getLastDayOfMonth(), status:"", createdBy: ""})}>RESET FILTERS</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 rounded-3">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="bg-primary text-white">
            <tr>
              <th className="ps-4">#</th>
              <th>TITLE</th>
              <th>REQUESTER</th>
              <th>TIME RANGE</th>
              <th>TOTAL</th>
              <th>STATUS</th>
              <th className="text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" /></td></tr> :
              currentItems.map((r, idx) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="ps-4 fw-bold">{indexOfFirstItem + idx + 1}</td>
                    <td className="fw-bold text-primary">{r.title}</td>
                    <td>{r.user?.employeeLeaveDetail?.fullName || r.user?.employeeName || "N/A"}</td>
                    <td className="small">
                      <div>{formatDate(r.startTime)}</div>
                      <div className="text-muted">to {formatDate(r.endTime)}</div>
                    </td>
                    <td><Badge bg="info">{r.totalHours}h</Badge></td>
                    <td>{renderStatus(r.status)}</td>
                    <td className="text-center">
                      <Button variant="link" size="sm" className="text-decoration-none" onClick={() => toggleRow(r.id)}>
                        {expandedRows[r.id] ? "[-] Hide Info" : "[+] Details"}
                      </Button>
                    </td>
                  </tr>
                  {expandedRows[r.id] && (
                    <tr className="bg-light">
                      <td colSpan="7" className="p-4 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <h6 className="fw-bold text-uppercase text-muted small">Content:</h6>
                            <p className="p-2 bg-white border rounded">{r.content}</p>
                          </div>
                          <div className="col-md-6">
                            <h6 className="fw-bold text-uppercase text-muted small">Approval Logs:</h6>
                            <div className="d-flex flex-wrap gap-2">
                              {r.approvalLogs?.sort((a,b) => a.stepOrder - b.stepOrder).map((log, i) => (
                                <div key={i} className="bg-white border rounded p-2 small shadow-sm">
                                  <strong>{log.approver?.employeeLeaveDetail?.fullName || log.approver?.employeeName || log.approver?.userName}</strong> - <Badge bg={log.status === "Approved" ? "success" : "warning"}>{log.status}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            }
          </tbody>
        </Table>
        <div className="p-3 d-flex justify-content-center">
          <Pagination size="sm">{[...Array(totalPages)].map((_, i) => <Pagination.Item key={i} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>)}</Pagination>
        </div>
      </Card>
    </div>
  );
};

export default OTAdminManagement;